import { ParamMap, Params } from '@angular/router'

import {
  AchievementFilter,
  OperatorListFilter,
  OperatorListSort,
} from './operator-list.logic'

export const DEFAULT_OPERATOR_SCORE_COUNT = 5

export const OPERATOR_LIST_QUERY_KEYS = {
  reportTo: 'reportTo',
  name: 'name',
  role: 'role',
  coaching: 'coaching',
  notAnswered: 'notAnswered',
  metric: 'metric',
  metricLogic: 'metricLogic',
  sort: 'sort',
  sortDir: 'sortDir',
  from: 'from',
  to: 'to',
  score: 'score',
} as const

// backend の MAX_MEMBER_AGGREGATION_MONTHS と揃える。
const MAX_PERIOD_MONTHS = 24
const KEYS = OPERATOR_LIST_QUERY_KEYS

export interface OperatorListQuery {
  filter: OperatorListFilter
  sort: OperatorListSort
  periodFrom: string
  periodTo: string
  scoreCount: number
}

interface OperatorListQueryOptions {
  roleKeys: readonly string[]
  itemIds: readonly string[]
  reportToUserIds: readonly string[]
  /** URL 復元時に限り、指定された役職がすべて無効なら全選択に戻す。 */
  resetMissingRolesToAll: boolean
}

/** URL を解釈し、取得条件を検証する。選択肢に依存する条件は取得後に検証する。 */
export function parseOperatorListQueryParams(
  params: ParamMap,
  defaultPeriod: string,
  scoreOptions: readonly number[],
): OperatorListQuery {
  let periodFrom = params.get(KEYS.from) ?? defaultPeriod
  let periodTo = params.get(KEYS.to) ?? defaultPeriod
  if (validatePeriodRange(periodFrom, periodTo) !== '') {
    periodFrom = defaultPeriod
    periodTo = defaultPeriod
  }
  const score = Number(params.get(KEYS.score))
  const coaching = params.get(KEYS.coaching)
  const metricStatuses = Object.fromEntries(
    params.getAll(KEYS.metric).flatMap<[string, AchievementFilter]>((value) => {
      const match = /^(.+):(achieved|unachieved)$/.exec(value)
      return match == null
        ? []
        : [[match[1], match[2] === 'achieved' ? 'achieved' : 'unachieved']]
    }),
  )
  return {
    filter: {
      name: params.get(KEYS.name) ?? '',
      roleKeys: params.has(KEYS.role)
        ? [...new Set(params.getAll(KEYS.role).filter((key) => key !== ''))]
        : null,
      coaching:
        coaching === 'included' || coaching === 'excluded' ? coaching : 'all',
      reportToUserId: params.get(KEYS.reportTo),
      response: params.get(KEYS.notAnswered) === '1' ? 'notAnswered' : 'all',
      metricStatuses,
      metricLogic: params.get(KEYS.metricLogic) === 'or' ? 'or' : 'and',
    },
    sort: parseSort(params),
    periodFrom,
    periodTo,
    scoreCount: scoreOptions.includes(score)
      ? score
      : DEFAULT_OPERATOR_SCORE_COUNT,
  }
}

/** 並び順を検証し、不正な列指定は名前の昇順に戻す。 */
function parseSort(params: ParamMap): OperatorListSort {
  const value = params.get(KEYS.sort)
  const direction = params.get(KEYS.sortDir) === 'desc' ? 'desc' : 'asc'
  if (value == null || value === 'name' || value === 'score') {
    return {
      field: value === 'score' ? 'score' : 'name',
      direction,
      itemId: null,
    }
  }
  const match = /^(actualRate|targetRate):(.+)$/.exec(value)
  if (match != null) {
    return {
      field: match[1] === 'actualRate' ? 'actualRate' : 'targetRate',
      direction,
      itemId: match[2],
    }
  }
  return { field: 'name', direction: 'asc', itemId: null }
}

/** 取得済みの選択肢に存在する条件だけを残す。役職省略時は新しい選択肢も全選択する。 */
export function validateOperatorListQuery(
  state: OperatorListQuery,
  options: OperatorListQueryOptions,
): OperatorListQuery {
  const selectedRoles = state.filter.roleKeys
  const roleKeys =
    selectedRoles == null
      ? null
      : options.roleKeys.filter((key) => selectedRoles.includes(key))
  const noValidRoles =
    options.resetMissingRolesToAll &&
    selectedRoles != null &&
    selectedRoles.length > 0 &&
    roleKeys?.length === 0
  const reportTo = state.filter.reportToUserId
  return {
    ...state,
    filter: {
      ...state.filter,
      roleKeys:
        noValidRoles ||
        (roleKeys != null &&
          options.roleKeys.length > 0 &&
          roleKeys.length === options.roleKeys.length)
          ? null
          : roleKeys,
      reportToUserId:
        reportTo != null && options.reportToUserIds.includes(reportTo)
          ? reportTo
          : null,
      metricStatuses: Object.fromEntries(
        options.itemIds.map((id) => [
          id,
          Object.hasOwn(state.filter.metricStatuses, id)
            ? state.filter.metricStatuses[id]
            : 'all',
        ]),
      ),
    },
    sort:
      state.sort.itemId != null && !options.itemIds.includes(state.sort.itemId)
        ? { field: 'name', direction: 'asc', itemId: null }
        : state.sort,
  }
}

/** 一覧の状態を、既定値を省略した URL パラメータへ変換する。 */
export function buildOperatorListQueryParams(
  state: OperatorListQuery,
  defaultPeriod: string,
): Params {
  const params: Params = {}
  const { filter, sort } = state
  if (filter.reportToUserId != null)
    params[KEYS.reportTo] = filter.reportToUserId
  if (filter.name !== '') params[KEYS.name] = filter.name
  if (filter.roleKeys != null)
    params[KEYS.role] =
      filter.roleKeys.length === 0 ? [''] : [...filter.roleKeys]
  if (filter.coaching !== 'all') params[KEYS.coaching] = filter.coaching
  if (filter.response === 'notAnswered') params[KEYS.notAnswered] = '1'
  const metrics = Object.entries(filter.metricStatuses)
    .filter(([, status]) => status !== 'all')
    .map(([id, status]) => `${id}:${status}`)
  if (metrics.length > 0) params[KEYS.metric] = metrics
  if (filter.metricLogic !== 'and')
    params[KEYS.metricLogic] = filter.metricLogic
  if (sort.field !== 'name')
    params[KEYS.sort] =
      sort.itemId == null ? sort.field : `${sort.field}:${sort.itemId}`
  if (sort.direction !== 'asc') params[KEYS.sortDir] = sort.direction
  if (state.periodFrom !== defaultPeriod || state.periodTo !== defaultPeriod) {
    params[KEYS.from] = state.periodFrom
    params[KEYS.to] = state.periodTo
  }
  if (state.scoreCount !== DEFAULT_OPERATOR_SCORE_COUNT)
    params[KEYS.score] = String(state.scoreCount)
  return params
}

/** 一覧で管理するキーのみ比較し、単一値と配列の表現の違いを吸収する。 */
export function areOperatorQueryParamsEqual(
  left: ParamMap,
  right: ParamMap,
): boolean {
  return Object.values(KEYS).every((key) =>
    areOperatorQueryParamValuesEqual(left, right, key),
  )
}

/** 指定したキーの値を、欠落・空文字・繰り返しの順序も含めて比較する。 */
export function areOperatorQueryParamValuesEqual(
  left: ParamMap,
  right: ParamMap,
  key: string,
): boolean {
  const leftValues = left.getAll(key)
  const rightValues = right.getAll(key)
  return (
    leftValues.length === rightValues.length &&
    leftValues.every((value, index) => value === rightValues[index])
  )
}

/** 集計期間の形式・前後関係・最大月数を検証し、正常時は空文字を返す。 */
export function validatePeriodRange(from: string, to: string): string {
  const pattern = /^\d{4}-(0[1-9]|1[0-2])$/
  if (!pattern.test(from) || !pattern.test(to))
    return 'KPI 集計期間を選択してください。'
  if (from > to) return 'KPI 集計期間の開始月は終了月以前にしてください。'
  const [fromYear, fromMonth] = from.split('-').map(Number)
  const [toYear, toMonth] = to.split('-').map(Number)
  if (
    toYear * 12 + toMonth - (fromYear * 12 + fromMonth) + 1 >
    MAX_PERIOD_MONTHS
  ) {
    return `KPI 集計期間は ${MAX_PERIOD_MONTHS} か月以内で指定してください。`
  }
  return ''
}
